import { keyframes } from "@mui/material";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { skipToken } from "@reduxjs/toolkit/query";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import ProposalField, { ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import DateRedlinePicker from "work/proposals/redlining/field-redlines/_views/date-redline-picker";
import TextRedlineInput from "work/proposals/redlining/field-redlines/_views/text-redline-input";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline from "work/proposals/redlining/proposal-redline";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ProposalContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
  },
  alignItems: "center",
  display: "grid",
  gap: theme.spacing(6),
  gridTemplateColumns: "auto auto",
  height: "100%",
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  width: "100%",
}));
const GeneralSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  marginLeft: theme.spacing(0.5),
}));
const DateSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  "& .MuiFormControl-root": {
    marginTop: theme.spacing(1),
  },
}));
const ClientTextField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(2, 0),
  minWidth: theme.spacing(50),
  width: "min-content",
}));
const NameTextField = styled(TextRedlineInput, { shouldForwardProp: (prop) => prop !== "active" })<{
  active?: boolean;
}>(({ theme, active }) => ({
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(2, 0),
  minWidth: theme.spacing(50),
  ...(active && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        animation: `${pulseBorder} 1.5s ease-in-out infinite`,
      },
    },
  }),
}));
const DescriptionTextField = styled(TextRedlineInput, { shouldForwardProp: (prop) => prop !== "active" })<{
  active?: boolean;
}>(({ theme, active }) => ({
  [theme.breakpoints.down("md")]: {
    minWidth: "unset",
    width: "100%",
  },
  margin: theme.spacing(2, 0),
  minWidth: theme.spacing(50),
  ...(active && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        animation: `${pulseBorder} 1.5s ease-in-out infinite`,
      },
    },
  }),
}));
const HighlightRedlineDatePicker = styled(DateRedlinePicker, {
  shouldForwardProp: (prop) => prop !== "active",
})<{
  active?: boolean;
}>(({ theme, active }) => ({
  margin: theme.spacing(2, 0),
  ...(active && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        animation: `${pulseBorder} 1.5s ease-in-out infinite`,
      },
    },
  }),
}));

type DetailsTabProps = {
  activeReviewField: ProposalField | undefined;
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  disableEditing?: boolean;
  commentThreads: CommentThread[];
  clientLeaderUserId: Guid | null;
  onProposalRedlineChange: (newRedline: ProposalRedline, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField: (fieldInfo: ProposalField | undefined) => void;
  onCommentsClicked: (field: ProposalField, title?: string) => void;
};

const DetailsTab = (props: Readonly<DetailsTabProps>) => {
  const {
    proposalRedline,
    activeReviewField,
    commentThreads,
    disableEditing,
    clientLeaderUserId,
    onProposalRedlineChange,
    onTraverseToNewField,
    onCommentsClicked,
  } = props;

  const clientLeaderIndividualQuery = useGetIndividualsByUserIdsQuery(
    clientLeaderUserId ? { userIds: new Set([clientLeaderUserId.value]) } : skipToken
  );

  const clientName = useMemo(() => {
    const clientLeader = Individual.fromJSON(clientLeaderIndividualQuery.data?.[0]);
    if (clientLeaderUserId === null) return "Masked";
    if (clientLeader) return clientLeader.fullName;
    if (clientLeaderIndividualQuery.isLoading) return "Loading...";
    if (clientLeaderIndividualQuery.isError) return "Error loading client";
    return "Unknown client";
  }, [clientLeaderIndividualQuery.data, clientLeaderIndividualQuery.isError, clientLeaderIndividualQuery.isLoading, clientLeaderUserId]);

  return (
    proposalRedline && (
      <ProposalContainer>
        <GeneralSection>
          {clientName && <ClientTextField label="Client" value={clientName} disabled />}
          <NameTextField
            focused={activeReviewField?.name === ProposalFieldName.Name}
            required={!disableEditing}
            active={activeReviewField?.isEqualTo(ProposalField.Name)}
            helperText={disableEditing ? "" : "required"}
            disabled={disableEditing}
            readOnly={disableEditing}
            textRedline={proposalRedline.name}
            issues={props.issues?.entries.filter((entry) => entry.field.isEqualTo(ProposalField.Name))}
            onCommentsClicked={() => onCommentsClicked(ProposalField.Name, undefined)}
            commentThreads={commentThreads}
            fieldConstructor={ProjectName}
            onTextFieldRedlineChange={(
              updatedRedline: FieldRedline<ProjectName>,
              traversalFieldOverride?: ProposalField | null,
            ) => {
              try {
                onProposalRedlineChange(
                  proposalRedline.updateNameRedline(updatedRedline), 
                  traversalFieldOverride
                );
              } catch (error) {
                console.error("error", error);
                enqueueSnackbar("Error updating proposal name", {
                  variant: "error",
                });
              }
            }}
            onTraverseIn={() => {
              onTraverseToNewField(ProposalField.Name);
            }}
          />

          <DescriptionTextField
            focused={activeReviewField?.name === ProposalFieldName.Description}
            active={activeReviewField?.isEqualTo(ProposalField.Description)}
            required={!disableEditing}
            multiline={true}
            helperText={disableEditing ? "" : "required"}
            disabled={disableEditing}
            readOnly={disableEditing}
            minRows={3}
            textRedline={proposalRedline.description}
            issues={props.issues?.entries.filter((entry) => entry.field.isEqualTo(ProposalField.Description))}
            onCommentsClicked={() => onCommentsClicked(ProposalField.Description, undefined)}
            commentThreads={commentThreads}
            fieldConstructor={ProjectDescription}
            onTextFieldRedlineChange={(
              updatedRedline: FieldRedline<ProjectDescription>,
              traversalFieldOverride?: ProposalField | null,
            ) => {
              try {
                onProposalRedlineChange(
                  proposalRedline.updateDescriptionRedline(updatedRedline),
                  traversalFieldOverride,
                );
              } catch (error) {
                console.error("error", error);
                enqueueSnackbar("Error updating proposal description", {
                  variant: "error",
                });
              }
            }}
            onTraverseIn={() => {
              onTraverseToNewField(ProposalField.Description);
            }}
          />
        </GeneralSection>
        <DateSection>
          <HighlightRedlineDatePicker
            focused={activeReviewField?.name === ProposalFieldName.ResponseDueBy}
            active={activeReviewField?.isEqualTo(ProposalField.ResponseDueBy)}
            disabled={disableEditing}
            readOnly={disableEditing}
            dateRedline={proposalRedline.responseDueBy}
            issues={props.issues?.entries.filter((entry) => entry.field.isEqualTo(ProposalField.ResponseDueBy))}
            minDate={new AHDate().asMoment}
            onCommentsClicked={() => onCommentsClicked(ProposalField.ResponseDueBy, undefined)}
            commentThreads={commentThreads}
            onDateRedlineChange={(updatedRedline: FieldRedline<AHDate>, traversalFieldOverride?: ProposalField) => {
              try {
                onProposalRedlineChange(
                  proposalRedline.updateResponseDueByRedline(updatedRedline),
                  traversalFieldOverride,
                );
              } catch (error) {
                console.error("error", error);
                enqueueSnackbar("Error updating proposal response due by", {
                  variant: "error",
                });
              }
            }}
            onTraverseIn={() => {
              onTraverseToNewField(ProposalField.ResponseDueBy);
            }}
          />

          <HighlightRedlineDatePicker
            focused={activeReviewField?.name === ProposalFieldName.StartDate}
            active={activeReviewField?.isEqualTo(ProposalField.StartDate)}
            disabled={disableEditing}
            dateRedline={proposalRedline.startDate}
            issues={props.issues?.entries.filter((entry) => entry.field.isEqualTo(ProposalField.StartDate))}
            minDate={proposalRedline.responseDueBy.currentEntry?.asMoment}
            onCommentsClicked={() => onCommentsClicked(ProposalField.StartDate, undefined)}
            commentThreads={commentThreads}
            onDateRedlineChange={(updatedRedline: FieldRedline<AHDate>, traversalFieldOverride?: ProposalField) => {
              try {
                onProposalRedlineChange(proposalRedline.updateStartDateRedline(updatedRedline), traversalFieldOverride);
              } catch (error) {
                console.error("error", error);
                enqueueSnackbar("Error updating proposal start date", {
                  variant: "error",
                });
              }
            }}
            onTraverseIn={() => {
              onTraverseToNewField(ProposalField.StartDate);
            }}
          />

          <HighlightRedlineDatePicker
            focused={activeReviewField?.name === ProposalFieldName.EndDate}
            active={activeReviewField?.isEqualTo(ProposalField.EndDate)}
            disabled={disableEditing}
            dateRedline={proposalRedline.endDate}
            issues={props.issues?.entries.filter((entry) => entry.field.isEqualTo(ProposalField.EndDate))}
            minDate={proposalRedline.startDate.currentEntry?.asMoment}
            onCommentsClicked={() => onCommentsClicked(ProposalField.EndDate, undefined)}
            commentThreads={commentThreads}
            onDateRedlineChange={(updatedRedline: FieldRedline<AHDate>, traversalFieldOverride?: ProposalField) => {
              try {
                onProposalRedlineChange(proposalRedline.updateEndDateRedline(updatedRedline), traversalFieldOverride);
              } catch (error) {
                console.error("error", error);
                enqueueSnackbar("Error updating proposal end date", {
                  variant: "error",
                });
              }
            }}
            onTraverseIn={() => {
              onTraverseToNewField(ProposalField.EndDate);
            }}
          />
        </DateSection>
      </ProposalContainer>
    )
  );
};

export default DetailsTab;
